import {
  createContext,
  useContext,
  useState,
  Dispatch,
  SetStateAction,
  ReactNode,
  ChangeEvent,
  MouseEvent,
  useEffect,
} from 'react';

import { IFlag } from '../container/store/utils/Flags';
import { addHours, addDays } from 'date-fns';
import { useSnackbar } from './Snackbar';

import { formatDescription } from '../container/task/utils/formats';
import { FormatDate, FormatDateHours, FormatHours } from '../utils/format/FormatDate';
import { http } from '../services/apiTask/Http.interceptor';
import { Pagination } from '../component/table/interfaces/Pagination';
import StoreList, { Store } from '../container/store/interfaces/Store';
import { Region } from '../container/scheduleMaintenance/regions/interfaces/Region';
import xlsx from 'json-as-xlsx';
import { HEAD_EXCEL } from '../container/task/utils/HEAD_CELL';
import { AxiosError } from 'axios';
import { getStoresMultiFlags } from '../container/store/Store.service';
import { useStore } from './Store';
import { getAllRegion } from '../container/scheduleMaintenance/regions/Region.service';

interface ILoadingExport {
  total: number;
  current: number;
  message: string;
}

interface useContextInterface {
  getAllTasks: () => Promise<void>;
  pagination: Pagination;
  listRegions: Region[];
  setListRegions: Dispatch<SetStateAction<Region[]>>;
  setTaskList: Dispatch<SetStateAction<any[]>>;
  taskList: any[];
  initialDate: Date;
  setInitialDate: Dispatch<SetStateAction<Date>>;
  finalDate: Date;
  setFinalDate: Dispatch<SetStateAction<Date>>;
  selectStore: string[];
  setListStore: Dispatch<SetStateAction<Store[]>>;
  setSelectStore: Dispatch<SetStateAction<string[]>>;
  getStorebtFlag: () => void;
  selectFlag: string[];
  setSelectFlag: Dispatch<SetStateAction<string[]>>;
  selectRegion: string[];
  setSelectRegion: Dispatch<SetStateAction<string[]>>;
  arrayFlags: IFlag[];
  listStore: Store[];
  loadingExport: ILoadingExport;
  statusTask: string;
  setStatusTask: Dispatch<SetStateAction<string>>;
  typeTask: string;
  setTypeTask: Dispatch<SetStateAction<string>>;
  verifyToGet: () => boolean;
  exportToJson: () => Promise<void>;
  total: number;
  request: boolean;
  handleChangePage: (_event: unknown, newPage: number) => void;
  handleRequestSort: (_event: MouseEvent<unknown>, property: string) => void;
  handleRowPerPage: (event: ChangeEvent<HTMLInputElement>) => void;
}

interface TaskComponent {
  children: ReactNode;
}

interface IResponse {
  total: number;
  query: any[];
}

interface IExcel {
  sheet: string;
  columns: Array<{ label: string; value: any }>;
  content: Array<any>;
}

interface IPayload {
  store?: string[];
  state?: string;
  type?: string | null;
  initialDate: string;
  finalDate: string;
  limit: number;
  skip: number;
  orderBy: string;
  asc: 1 | -1;
}

const initDate = (): Date => {
  let now = new Date();
  now.setDate(now.getDate() - 7);
  return new Date(FormatHours(now, '00:00:00'));
};

const endDate = () => {
  return new Date(FormatHours(new Date(), '23:59:59'));
};

const TaskContext = createContext<useContextInterface>({} as useContextInterface);

export default function TaskProvider({ children }: TaskComponent) {
  const { arrayFlags } = useStore();

  const { setSnackbar } = useSnackbar();

  const [request, setRequest] = useState<boolean>(false);
  const [initialDate, setInitialDate] = useState<Date>(initDate());
  const [finalDate, setFinalDate] = useState<Date>(endDate());
  const [taskList, setTaskList] = useState<any[]>([]);
  const [listStore, setListStore] = useState<Store[]>([]);
  const [listRegions, setListRegions] = useState<Region[]>([]);

  const [selectStore, setSelectStore] = useState<string[]>([]);
  const [selectFlag, setSelectFlag] = useState<string[]>([]);
  const [selectRegion, setSelectRegion] = useState<string[]>([]);

  const [statusTask, setStatusTask] = useState('TODOS');
  const [typeTask, setTypeTask] = useState('TODOS');

  const [loadingExport, setLoadingExport] = useState({
    total: 0,
    current: 0,
    message: '',
  });

  const [total, setTotal] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({
    lastPage: 1,
    page: 1,
    sort: 'created_at',
    order: -1,
    perPage: 30,
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    if (verifyToGet()) {
      setPagination({ ...pagination, page: newPage + 1 });
    }
  };

  const handleRowPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    if (verifyToGet()) {
      setPagination({
        ...pagination,
        perPage: parseInt(event.target.value),
        page: 1,
      });
    }
  };

  const handleRequestSort = (_event: MouseEvent<unknown>, property: string) => {
    const isAsc = pagination.sort === property && pagination.order === 1;
    if (verifyToGet()) {
      setPagination({
        ...pagination,
        sort: property,
        order: isAsc ? -1 : 1,
        page: 1,
      });
    }
  };

  const handleGetFlag = (storeId: string): string => {
    try {
      const store = listStore.find((st) => st.id === storeId);
      const flagStore = arrayFlags.find((flag) => store && `${flag.id}` === `${store.flag_id}`);
      return flagStore ? (flagStore.flag as string) : '';
    } catch (error) {}
    return '';
  };

  const handleGetRegion = (storeId: string): string => {
    try {
      const store = listStore.find((store) => store.id === storeId);
      const regionStore = listRegions.find((region) => store && region.id === store.region_id);
      return regionStore ? regionStore.description : '';
    } catch (error) {}
    return '';
  };

  const getOffSet = () => {
    const offset = new Date().getTimezoneOffset() / 60;

    if (offset === 0) {
      return -3;
    } else {
      return 0;
    }
  };

  const handleGetTasks = async (payload: IPayload): Promise<IResponse> => {
    const taskResponse: IResponse = { query: [], total: 0 };

    try {
      const response = await http.post('/search', payload);

      if (response.data) {
        if (response.data.total > 0) {
          const formatedData = response.data.query.map((item: any, index: number) => {
            const created_at = addHours(new Date(item.created_at), getOffSet());
            const updated_at = addHours(new Date(item.updated_at), getOffSet());
            const flag_description = handleGetFlag(item.store);
            const region_description = handleGetRegion(item.store);
            const formated = {
              ...item,
              sap_code: item.sap_code ? parseInt(item.sap_code) : '',
              ean: item.ean ? parseInt(item.ean) : '',
              flag: flag_description,
              region: region_description,
              type: formatDescription(item.type || ''),
              origin: formatDescription(item.origin || ''),
              created_at: FormatDateHours(created_at),
              state:
                item.state === 'COMPLETED'
                  ? 'FINALIZADA'
                  : item.state === 'PENDING'
                  ? 'PENDENTE'
                  : item.state === 'CANCELED'
                  ? 'CANCELADA'
                  : 'SUBSTITUÍDA',
              unit_measurement: item.unit_measurement ? item.unit_measurement.toUpperCase() : '',
              date_closed: item.date_closed
                ? FormatDateHours(addHours(new Date(item.date_closed), getOffSet()))
                : '',
              product_was_gondola:
                item.state === 'COMPLETED' ? (item.product_was_gondola ? 'SIM' : 'NÃO') : '',
              reason: item.reason !== undefined ? item.reason.toUpperCase() || '' : '',
              date_brigade: item.date_brigade
                ? FormatDate(addDays(new Date(item.date_brigade), 1))
                : null,
              date_validity: item.date_validity
                ? FormatDate(addDays(new Date(item.date_validity), 1))
                : null,
              lost_sales: item.lost_sales
                ? item.lost_sales
                : ['FRC', 'FRCC'].includes(item.origin)
                ? 1
                : 0,
              qtd_n_entregue: item.qtd_n_entregue ?? 0,
              qtd_entregue: (item.quantity_sale ?? 0) - (item.qtd_n_entregue ?? 0),
              qtd_replaced: item.qtd_replaced ?? 0,
              quantity_sale_nominal: item.quantity_sale_nominal ?? item.quantity_sale ?? 0,
              qtd_n_entregue_nominal: item.qtd_n_entregue_nominal ?? item.qtd_n_entregue ?? 0,
              replaced_at: item.state === 'REPLACED' ? FormatDateHours(updated_at) : '',
              is_scanned: item.type.toUpperCase() === 'RG' ? (item.is_scanned ? 'SIM' : 'NÃO') : '',
            };
            return formated;
          });
          taskResponse.query = formatedData;
          taskResponse.total = response.data.total;
        }
      }
    } catch (error) {
      console.error(error);
      setSnackbar({ msg: 'Erro ao consultar tarefas!', type: 'error' });
    }

    return taskResponse;
  };

  const formatPayload = (): IPayload => {
    let payload: IPayload = {
      initialDate: new Date(initialDate).toISOString(),
      finalDate: new Date(finalDate).toISOString(),
      store: selectStore.filter((item) => item !== 'TODAS'),
      limit: pagination.perPage || 0,
      skip: ((pagination.page || 1) - 1) * (pagination.perPage || 30),
      orderBy: pagination.sort
        ? ['date', 'hours'].includes(pagination.sort)
          ? 'created_at'
          : pagination.sort === 'description'
          ? 'description_product'
          : pagination.sort
        : 'created_at',
      asc: pagination.order === 1 ? 1 : -1,
    };

    if (!['TODOS', null, ''].includes(String(statusTask))) {
      payload.state =
        statusTask === 'FINALIZADAS'
          ? 'COMPLETED'
          : statusTask === 'PENDENTES'
          ? 'PENDING'
          : statusTask === 'CANCELADAS'
          ? 'CANCELED'
          : 'REPLACED';
    }

    if (!['TODOS', null, ''].includes(String(typeTask))) {
      payload.type =
        typeTask === 'ALERTA DE VALIDADE'
          ? 'av'
          : typeTask === 'ALERTA DE PERDA DE VENDA'
          ? 'apv'
          : typeTask === 'REABASTECIMENTO DE GÔNDOLA'
          ? 'rg'
          : typeTask === 'EMISSÃO DE ETIQUETA'
          ? 'ee'
          : null;
      if (payload.type === null) delete payload.type;
    }

    return payload;
  };

  const getAllTasks = async () => {
    let payload: IPayload = formatPayload();

    if (payload.store && payload.store.length > 0 && payload.initialDate && payload.finalDate) {
      setRequest(true);
      const response: IResponse = await handleGetTasks(payload);
      setTotal(response.total);
      setTaskList(response.query);
      setRequest(false);
    }
  };

  const getTasksAllPages = async (): Promise<any[]> => {
    let payload: IPayload = formatPayload();

    let taskBigList: any[] = [];
    let keepWhile = true;

    payload.limit = 1000;
    payload.skip = 0;

    setLoadingExport({ ...loadingExport, message: 'Coletando tarefas' });
    while (keepWhile === true) {
      const response: IResponse = await handleGetTasks(payload);
      if (response.query.length < payload.limit) {
        keepWhile = false;
      } else {
        payload.skip = payload.skip + payload.limit;
      }

      if (response.query.length > 0) {
        taskBigList = taskBigList.concat(response.query);
      }

      setLoadingExport({
        total: response.total,
        current: taskBigList.length,
        message: `${taskBigList.length} de ${response.total} tarefas coletadas`,
      });
    }

    setLoadingExport({
      total: loadingExport.total,
      current: loadingExport.total,
      message: 'Gerando arquivo',
    });

    return taskBigList;
  };

  const exportToJson = async () => {
    try {
      let data: IExcel[] = [
        {
          sheet: 'Tarefas',
          columns: [],
          content: await getTasksAllPages(),
        },
      ];

      HEAD_EXCEL.forEach((r) => {
        data[0].columns.push({
          label: r.label,
          value: r.key,
        });
      });

      let settings = {
        fileName: 'tarefas',
      };
      xlsx(data, settings);
      setLoadingExport({
        total: 0,
        current: 0,
        message: '',
      });
    } catch (error) {
      setLoadingExport({
        total: 0,
        current: 0,
        message: '',
      });
      console.log('Error when generate EXCEL REPORT', error);
    }
  };

  const filter = (store: StoreList[]): any[] => {
    let storeAux: any[] = [];
    store.map((el: StoreList) => {
      storeAux.push(el.Loja);
      return el;
    });
    return storeAux;
  };

  const verifyToGet = (): boolean => {
    if (
      new Date(initialDate).getFullYear() === 1969 ||
      new Date(finalDate).getFullYear() === 1969
    ) {
      setSnackbar({
        msg: 'É necessário informar o intervalo de datas',
        type: 'error',
      });
      return false;
    }

    if (selectStore.filter((item) => item !== 'TODAS').length === 0) {
      setSnackbar({ msg: 'É necessário informar uma loja', type: 'error' });
      return false;
    }

    return true;
  };

  const getStorebtFlag = async () => {
    if (selectFlag.length > 0) {
      const listFlags: string[] = [];

      arrayFlags.forEach((a: IFlag) => {
        if (selectFlag.includes(a.flag as string)) {
          listFlags.push(a.id as string);
        }
      });

      setRequest(true);
      await getStoresMultiFlags({ flagIdList: listFlags })
        .then((res) => {
          if (res.data) {
            setListStore(filter(res.data as any));
          }
        })
        .catch((error: AxiosError) => {
          setRequest(false);
          console.log(error.response?.data);
          console.log(error);
          setSnackbar({
            msg: 'Erro ao consultar lojas da bandeira!',
            type: 'error',
          });
        })
        .finally(() => {
          setRequest(false);
        });
    }
  };

  useEffect(() => {
    getAllTasks();
  }, [pagination]);

  useEffect(() => {
    getAllRegion().then((r) => {
      setListRegions(r.data);
    });
  }, []);

  // clear if filter terms are change
  useEffect(() => {
    setTaskList([]);
  }, [initialDate, finalDate, selectStore, statusTask, typeTask]);

  useEffect(() => {
    setListStore([]);
    setSelectRegion([]);
    setSelectStore([]);
    setTaskList([]);
    getStorebtFlag();
  }, [selectFlag]);

  return (
    <TaskContext.Provider
      value={{
        getAllTasks,
        pagination,
        listRegions,
        setListRegions,
        taskList,
        setTaskList,
        initialDate,
        setInitialDate,
        finalDate,
        setFinalDate,
        selectStore,
        setListStore,
        setSelectStore,
        getStorebtFlag,
        selectFlag,
        setSelectFlag,
        arrayFlags,
        listStore,
        loadingExport,
        statusTask,
        setStatusTask,
        typeTask,
        setTypeTask,
        verifyToGet,
        exportToJson,
        total,
        request,
        handleChangePage,
        handleRequestSort,
        handleRowPerPage,
        selectRegion,
        setSelectRegion,
      }}
    >
      {' '}
      {children}{' '}
    </TaskContext.Provider>
  );
}

export function useTask() {
  const context = useContext<useContextInterface>(TaskContext);
  if (!context) throw new Error('Error must be used within a TaskProvider');
  const {
    getAllTasks,
    pagination,
    listRegions,
    setListRegions,
    taskList,
    setTaskList,
    initialDate,
    setInitialDate,
    finalDate,
    setFinalDate,
    selectStore,
    setListStore,
    setSelectStore,
    getStorebtFlag,
    selectFlag,
    setSelectFlag,
    arrayFlags,
    listStore,
    loadingExport,
    statusTask,
    setStatusTask,
    typeTask,
    setTypeTask,
    verifyToGet,
    exportToJson,
    total,
    request,
    handleChangePage,
    handleRequestSort,
    handleRowPerPage,
    selectRegion,
    setSelectRegion,
  } = context;
  return {
    getAllTasks,
    pagination,
    listRegions,
    setListRegions,
    setTaskList,
    taskList,
    initialDate,
    setInitialDate,
    finalDate,
    setFinalDate,
    selectStore,
    setListStore,
    setSelectStore,
    getStorebtFlag,
    selectFlag,
    setSelectFlag,
    arrayFlags,
    listStore,
    loadingExport,
    statusTask,
    setStatusTask,
    typeTask,
    setTypeTask,
    verifyToGet,
    exportToJson,
    total,
    request,
    handleChangePage,
    handleRequestSort,
    handleRowPerPage,
    selectRegion,
    setSelectRegion,
  };
}
